import { logger } from '@/lib/logger';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useState, useEffect, useMemo } from 'react';

export interface GlobalSearchResult {
  result_type: 'asset' | 'cable' | 'network' | 'subnet' | 'datacenter' | 'site';
  id: string;
  title: string;
  subtitle: string | null;
  url: string;
  status: string | null;
  rank: number;
}

export interface RecentSearch {
  id: string;
  search_query: string;
  result_count: number;
  searched_at: string;
}

/**
 * Full-text global search using server-side search_global RPC
 * Provides sub-200ms search performance with ranking
 */
export function useGlobalSearch(query: string, limit = 50) {
  const { companyId } = useAuth();
  const [debouncedQuery, setDebouncedQuery] = useState(query);
  
  // Debounce the search query (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query);
    }, 300);
    
    return () => clearTimeout(timer);
  }, [query]);
  
  const searchQuery = useQuery({
    queryKey: ['global-search', debouncedQuery, limit, companyId],
    queryFn: async () => {
      if (!companyId || debouncedQuery.length < 2) {
        return [];
      }
      
      const { data, error } = await supabase.rpc('search_global', {
        search_query: debouncedQuery,
        p_company_id: companyId,
        p_limit: limit,
      });
      
      if (error) {
        logger.error('useGlobalSearch', 'Global search error', error);
        throw error;
      }
      
      return (data || []) as GlobalSearchResult[];
    },
    enabled: !!companyId && debouncedQuery.length >= 2,
    staleTime: 10 * 1000, // 10 seconds
  });

  return {
    ...searchQuery,
    debouncedQuery,
  };
}

/**
 * Hook to fetch recent searches for the current user
 */
export function useRecentSearches(limit = 10) {
  const { user, companyId } = useAuth();
  const userId = user?.id;
  
  return useQuery({
    queryKey: ['recent-searches', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recent_searches')
        .select('id, search_query, result_count, searched_at')
        .order('searched_at', { ascending: false })
        .limit(limit);
      
      if (error) throw error;
      return data as RecentSearch[];
    },
    enabled: !!userId && !!companyId,
    staleTime: 30 * 1000, // 30 seconds
  });
}

/**
 * Hook to save a search to recent searches
 */
export function useSaveRecentSearch() {
  const { user, companyId } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ searchQuery, resultCount }: { searchQuery: string; resultCount: number }) => {
      if (!userId || !companyId || searchQuery.length < 2) return;
      
      // Upsert - update if exists, insert if not
      const { error } = await supabase
        .from('recent_searches')
        .upsert(
          {
            user_id: userId,
            company_id: companyId,
            search_query: searchQuery,
            result_count: resultCount,
            searched_at: new Date().toISOString(),
          },
          { onConflict: 'user_id,search_query' }
        );
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recent-searches'] });
    },
  });
}

/**
 * Hook to clear all recent searches
 */
export function useClearRecentSearches() {
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async () => {
      if (!userId) return;
      
      const { error } = await supabase
        .from('recent_searches')
        .delete()
        .eq('user_id', userId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recent-searches'] });
    },
  });
}

/**
 * Hook to delete a single recent search
 */
export function useDeleteRecentSearch() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (searchId: string) => {
      const { error } = await supabase
        .from('recent_searches')
        .delete()
        .eq('id', searchId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recent-searches'] });
    },
  });
}

/**
 * Generate search suggestions based on query prefix
 */
export function useSearchSuggestions(query: string) {
  const { data: recentSearches } = useRecentSearches();
  
  const suggestions = useMemo(() => {
    if (!query || query.length < 1 || !recentSearches) return [];
    
    const lowerQuery = query.toLowerCase();
    return recentSearches
      .filter(s => s.search_query.toLowerCase().startsWith(lowerQuery))
      .slice(0, 5)
      .map(s => s.search_query);
  }, [query, recentSearches]);
  
  return suggestions;
}
